import React, {
  useState,
  useRef
} from 'react';
import PropTypes from 'prop-types';
import { Dropdown } from 'primereact/dropdown';
import { InputTextarea } from 'primereact/inputtextarea';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Grid } from '@mui/material';
import { Toast } from 'primereact/toast';
import './StringBuilder.css'; // your original CSS

/**
 * StringBuilder lets users compose expressions like "${var.method(arg)}",
 * with a special two-argument UI for rng(min,max).
 *
 * @param {object} props
 * @param {string} [props.backgroundColor='#5D3B9E']  Base hex for styling
 * @param {object} [props.userSettings={ customVariables: {} }]
 *        Provides any custom variables the user has defined
 */
const StringBuilder = ({
  backgroundColor = '#5D3B9E',
  userSettings = {}
}) => {
  const customVars = userSettings.customVariables || {};

  // Main builder state
  const [variableType, setVariableType] = useState(null);
  const [variable, setVariable]         = useState(null);
  const [method, setMethod]             = useState(null);
  const [argument, setArgument]         = useState('');
  const [minArg, setMinArg]             = useState('');
  const [maxArg, setMaxArg]             = useState('');
  const [nestedTarget, setNestedTarget] = useState(null);
  const [expression, setExpression]     = useState('');
  const [explanation, setExplanation]   = useState('');
  const [showNestedBuilder, setShowNestedBuilder] = useState(false);

  // Toast
  const toast = useRef(null);

  // Refs & selection
  const argumentRef = useRef(null);
  const minArgRef   = useRef(null);
  const maxArgRef   = useRef(null);
  const [selStart, setSelStart] = useState(0);
  const [selEnd,   setSelEnd]   = useState(0);

  // Color styles
  const hex = backgroundColor.replace('#','');
  const [r, g, b] = [
    parseInt(hex.slice(0,2),16),
    parseInt(hex.slice(2,4),16),
    parseInt(hex.slice(4,6),16)
  ];
  const styles = {
    dropdown:  { backgroundColor: `rgba(${r},${g},${b},0.75)` },
    textfield: { backgroundColor: `rgba(${r},${g},${b},0.75)` }
  };

  // Variables by type
  const getVariablesOfType = type => {
    const out = [];
    if (type === 'system') {
      out.push({ label: 'Random Number', value: 'rng' });
    }
    Object.entries(customVars).forEach(([name, def]) => {
      if (def.datatype === type) {
        out.push({ label: def.title, value: name });
      }
    });
    return out;
  };
  const variableTypes = [
    { label: 'Number',  value: 'number'  },
    { label: 'Text',    value: 'text'    },
    { label: 'Boolean', value: 'boolean' },
    { label: 'List',    value: 'list'    },
    { label: 'System',  value: 'system'  }
  ];
  const variables = {
    number:  getVariablesOfType('number'),
    text:    getVariablesOfType('text'),
    boolean: getVariablesOfType('boolean'),
    list:    getVariablesOfType('list'),
    system:  getVariablesOfType('system')
  };

  // Methods and args requirement
  const methodOptions = {
    number:   ['read','increment','decrement','set'],
    text:     ['read','set'],
    boolean:  ['read','toggle','set'],
    list:     ['read','add','set','delete','includes','getposition'],
    system:   ['rng']
  };
  const methods = {
    number:   [
      { label: 'read',      value: 'read'      },
      { label: 'increment', value: 'increment' },
      { label: 'decrement', value: 'decrement' },
      { label: 'set',       value: 'set'       }
    ],
    text:     [
      { label: 'read', value: 'read' },
      { label: 'set',  value: 'set'  }
    ],
    boolean:  [
      { label: 'read',   value: 'read'   },
      { label: 'toggle', value: 'toggle' },
      { label: 'set',    value: 'set'    }
    ],
    list:     [
      { label: 'read',       value: 'read'       },
      { label: 'length',     value: 'length'     },
      { label: 'add',        value: 'add'        },
      { label: 'set',        value: 'set'        },
      { label: 'delete',     value: 'delete'     },
      { label: 'includes',   value: 'includes'   },
      { label: 'getposition',value: 'getposition'},
      { label: 'clear',      value: 'clear'      }
    ],
    system:   [
      { label: 'rng', value: 'rng' }
    ]
  };
  const methodRequiresArgument = (m, type) => methodOptions[type]?.includes(m);

  // Nested insert for main builder
  const handleNestedInsert = nestedExpr => {
    let before, after, newVal, ref;
    switch (nestedTarget) {
      case 'min':
        before = minArg.slice(0, selStart);
        after  = minArg.slice(selEnd);
        newVal = before + nestedExpr + after;
        setMinArg(newVal);
        ref = minArgRef;
        break;
      case 'max':
        before = maxArg.slice(0, selStart);
        after  = maxArg.slice(selEnd);
        newVal = before + nestedExpr + after;
        setMaxArg(newVal);
        ref = maxArgRef;
        break;
      default:
        before = argument.slice(0, selStart);
        after  = argument.slice(selEnd);
        newVal = before + nestedExpr + after;
        setArgument(newVal);
        ref = argumentRef;
    }
    const pos = selStart + nestedExpr.length;
    setTimeout(() => {
      if (ref.current) {
        ref.current.setSelectionRange(pos, pos);
        ref.current.focus();
      }
    }, 0);
    setShowNestedBuilder(false);
    setNestedTarget(null);
  };

  // Build snippet
  const handleAddToExpression = () => {
    let snippet;
    if (method === 'rng') snippet = `\${rng(${minArg},${maxArg})}`;
    else {
      snippet = '${' + variable + '.' + method;
      if (argument) snippet += `.${argument}`;
      snippet += '}';
    }
    setExpression(prev => prev ? prev + snippet : snippet);
    setVariableType(null);
    setVariable(null);
    setMethod(null);
    setArgument('');
    setMinArg('');
    setMaxArg('');
  };

  // Clear and copy
  const handleClearExpression = () => { setExpression(''); setExplanation(''); };
  const handleExpressionClick = e => {
    e.target.select();
    navigator.clipboard.writeText(expression).then(() => {
      toast.current.show({ severity:'success', summary:'Copied', detail:'Expression copied', life:2000 });
    });
  };

  return (
    <div className={`p-fluid ${showNestedBuilder ? 'blur-background' : ''}`} style={{ minWidth:'350px', maxWidth: '600px', margin:'0 auto' }}>
      <Toast position='bottom-left' ref={toast} />
      <h3>String Builder</h3>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <label htmlFor="variableType">Variable Type</label>
          <Dropdown id="variableType" value={variableType} options={variableTypes}
            onChange={e=>{ setVariableType(e.value); setVariable(null); setMethod(null); }}
            placeholder="Select Variable Type" style={styles.dropdown} />
        </Grid>
        {variableType && <Grid item xs={12}><label htmlFor="variable">Variable</label>
          <Dropdown id="variable" value={variable} options={variables[variableType]}
            onChange={e=>setVariable(e.value)} placeholder="Select Variable"
            optionLabel="label" optionValue="value" style={styles.dropdown} />
        </Grid>}
        {variable && <Grid item xs={12}><label htmlFor="method">Method</label>
          <Dropdown id="method" value={method} options={methods[variableType]}
            onChange={e=>setMethod(e.value)} placeholder="Select Method"
            optionLabel="label" optionValue="value" style={styles.dropdown} />
        </Grid>}
        {method && methodRequiresArgument(method, variableType) && (method==='rng' ? (
          <>
            <Grid item xs={6}><label htmlFor="minArg">Min</label>
              <InputTextarea id="minArg" ref={minArgRef} value={minArg}
                onChange={e=>setMinArg(e.target.value)}
                onSelect={e=>{ setSelStart(e.target.selectionStart); setSelEnd(e.target.selectionEnd); }}
                rows={1} autoResize placeholder="Enter minimum value" style={styles.textfield} />
              <Button className="p-button-text p-mr-2" label="Insert Variable"
                onClick={()=>{ setNestedTarget('min'); setShowNestedBuilder(true); }} />
            </Grid>
            <Grid item xs={6}><label htmlFor="maxArg">Max</label>
              <InputTextarea id="maxArg" ref={maxArgRef} value={maxArg}
                onChange={e=>setMaxArg(e.target.value)}
                onSelect={e=>{ setSelStart(e.target.selectionStart); setSelEnd(e.target.selectionEnd); }}
                rows={1} autoResize placeholder="Enter maximum value" style={styles.textfield} />
              <Button className="p-button-text p-mr-2" label="Insert Variable"
                onClick={()=>{ setNestedTarget('max'); setShowNestedBuilder(true); }} />
            </Grid>
          </>
        ) : (
          <Grid item xs={12}><label htmlFor="argument">Argument</label>
            <InputTextarea id="argument" ref={argumentRef} value={argument}
              onChange={e=>setArgument(e.target.value)}
              onSelect={e=>{ setSelStart(e.target.selectionStart); setSelEnd(e.target.selectionEnd); }}
              rows={3} autoResize placeholder="Enter Argument (supports mathematical expressions)" style={styles.textfield} />
            <Button className="p-button-text p-mr-2" icon="pi pi-plus" label="Insert Variable"
              onClick={()=>{ setNestedTarget('argument'); setShowNestedBuilder(true); }} />
          </Grid>
        ))}
        <Grid item xs={12} style={{ marginTop:16, display:'flex', gap:12 }}>
          <Button label="Add to Expression" icon="pi pi-check" onClick={handleAddToExpression}
            disabled={!method} />
          <Button label="Clear Expression" icon="pi pi-times" className="p-button-secondary"
            onClick={handleClearExpression} disabled={!expression} />
        </Grid>
        {expression && <> <Grid item xs={12}><h4>Current Expression</h4></Grid>
          <Grid item xs={12}><InputTextarea value={expression} readOnly rows={3} autoResize
            style={styles.textfield} onClick={handleExpressionClick} /></Grid>
        </>}
      </Grid>

      {/* Nested builder dialog */}
      <Dialog header="Nested Variable Builder" visible={showNestedBuilder} modal style={{ width:'50vw' }} onHide={()=>setShowNestedBuilder(false)}>
        <NestedVariableBuilder
          onInsert={handleNestedInsert}
          variables={variables}
          variableTypes={variableTypes}
          methods={methods}
          methodRequiresArgument={methodRequiresArgument}
          styles={styles}
        />
      </Dialog>
    </div>
  );
};

StringBuilder.propTypes = {
  backgroundColor: PropTypes.string,
  userSettings:    PropTypes.shape({ customVariables: PropTypes.object })
};

/**
 * NestedVariableBuilder allows nested "${var.method(arg)}" inserts,
 * and now handles rng(min,max) too.
 */
const NestedVariableBuilder = ({
  onInsert,
  variables,
  variableTypes,
  methods,
  methodRequiresArgument,
  styles
}) => {
  const [vType, setVType]   = useState(null);
  const [variable, setVar]  = useState(null);
  const [method, setMeth]   = useState(null);
  const [arg, setArg]       = useState('');
  const [minArg, setMinArg] = useState('');
  const [maxArg, setMaxArg] = useState('');
  const [nestedTarget, setNestedTarget] = useState(null);
  const [showNested, setShowNested] = useState(false);

  const argRef = useRef(null);
  const minRef = useRef(null);
  const maxRef = useRef(null);
  const [selStart, setSelStart] = useState(0);
  const [selEnd,   setSelEnd]   = useState(0);

  // Handle deeper nested insert
  const handleNested = nestedExpr => {
    let before, after, newVal, ref;
    switch (nestedTarget) {
      case 'min':
        before = minArg.slice(0, selStart);
        after  = minArg.slice(selEnd);
        newVal = before + nestedExpr + after;
        setMinArg(newVal);
        ref = maxRef; // focus after
        break;
      case 'max':
        before = maxArg.slice(0, selStart);
        after  = maxArg.slice(selEnd);
        newVal = before + nestedExpr + after;
        setMaxArg(newVal);
        ref = minRef;
        break;
      default:
        before = arg.slice(0, selStart);
        after  = arg.slice(selEnd);
        newVal = before + nestedExpr + after;
        setArg(newVal);
        ref = argRef;
    }
    const pos = selStart + nestedExpr.length;
    setTimeout(() => {
      if (ref.current) {
        ref.current.setSelectionRange(pos, pos);
        ref.current.focus();
      }
    }, 0);
    setShowNested(false);
    setNestedTarget(null);
  };

  // Insert final expression
  const handleInsert = () => {
    let expr;
    if (method === 'rng') expr = `\${rng(${minArg},${maxArg})}`;
    else {
      expr = '${' + variable + '.' + method;
      const useArg = methodRequiresArgument(method, vType);
      if (useArg && arg) expr += `.${arg}`;
      expr += '}';
    }
    onInsert(expr);
  };

  return (
    <div className="p-fluid" style={{ width:'100%', margin:'0 auto' }}>
      <Grid container spacing={2}>
        {/* Type */}
        <Grid item xs={12}>
          <label htmlFor="nestedVariableType">Variable Type</label>
          <Dropdown id="nestedVariableType" value={vType} options={variableTypes}
            onChange={e=>{ setVType(e.value); setVar(null); setMeth(null); }}
            placeholder="Select Variable Type" style={styles.dropdown} />
        </Grid>
        {/* Variable */}
        {vType && <Grid item xs={12}><label htmlFor="nestedVariable">Variable</label>
          <Dropdown id="nestedVariable" value={variable} options={variables[vType]}
            onChange={e=>setVar(e.value)} placeholder="Select Variable"
            optionLabel="label" optionValue="value" style={styles.dropdown} />
        </Grid>}
        {/* Method */}
        {variable && <Grid item xs={12}><label htmlFor="nestedMethod">Method</label>
          <Dropdown id="nestedMethod" value={method} options={methods[vType]}
            onChange={e=>setMeth(e.value)} placeholder="Select Method"
            optionLabel="label" optionValue="value" style={styles.dropdown} />
        </Grid>}
        {/* Arguments or rng */}
        {method && methodRequiresArgument(method, vType) && ( method==='rng' ? (
          <>
            <Grid item xs={6}><label>Min</label>
              <InputTextarea ref={minRef} value={minArg}
                onChange={e=>setMinArg(e.target.value)}
                onSelect={e=>{ setSelStart(e.target.selectionStart); setSelEnd(e.target.selectionEnd); }}
                rows={1} autoResize placeholder="Min" style={styles.textfield} />
              <Button className="p-button-text p-mr-2" label="Insert Variable"
                onClick={()=>{ setNestedTarget('min'); setShowNested(true); }} />
            </Grid>
            <Grid item xs={6}><label>Max</label>
              <InputTextarea ref={maxRef} value={maxArg}
                onChange={e=>setMaxArg(e.target.value)}
                onSelect={e=>{ setSelStart(e.target.selectionStart); setSelEnd(e.target.selectionEnd); }}
                rows={1} autoResize placeholder="Max" style={styles.textfield} />
              <Button className="p-button-text p-mr-2" label="Insert Variable"
                onClick={()=>{ setNestedTarget('max'); setShowNested(true); }} />
            </Grid>
          </>
        ) : (
          <Grid item xs={12}><label>Argument</label>
            <InputTextarea ref={argRef} value={arg}
              onChange={e=>setArg(e.target.value)}
              onSelect={e=>{ setSelStart(e.target.selectionStart); setSelEnd(e.target.selectionEnd); }}
              rows={3} autoResize placeholder="Argument" style={styles.textfield} />
            <Button className="p-button-text p-mr-2" icon="pi pi-plus" label="Insert Variable"
              onClick={()=>{ setNestedTarget('default'); setShowNested(true); }} />
          </Grid>
        ))}
        <Grid item xs={12}>
          <Button icon="pi pi-check" label="Insert" onClick={handleInsert} disabled={!method} />
        </Grid>
      </Grid>
      {/* deeper nested */}
      <Dialog header="Nested Variable Builder" visible={showNested} modal style={{ width:'40vw' }}
        onHide={()=>setShowNested(false)}>
        <NestedVariableBuilder
          onInsert={handleNested}
          variables={variables}
          variableTypes={variableTypes}
          methods={methods}
          methodRequiresArgument={methodRequiresArgument}
          styles={styles}
        />
      </Dialog>
    </div>
  );
};

NestedVariableBuilder.propTypes = {
  onInsert:               PropTypes.func.isRequired,
  variables:              PropTypes.object.isRequired,
  variableTypes:          PropTypes.array.isRequired,
  methods:                PropTypes.object.isRequired,
  methodRequiresArgument: PropTypes.func.isRequired,
  styles:                 PropTypes.object.isRequired
};

export default StringBuilder;